import React, { memo, useMemo, useCallback } from 'react';
import { FloralCard, FloralErrorBoundary } from './FloralCard';
import { FloralInput, FloralSelect, FloralButton } from './FormComponents';
import { FloralCornerDecoration, FloralDivider, FloralHeader, WatercolorBackground } from './FloralDecorations';
import { useLazyAssetWithIntersection, useCachedSVG } from '../../hooks/useLazyAssets';
import { FloralCardProps, FloralHeaderProps } from '../../types/ui';

// Memoized FloralCard with performance optimizations
export const OptimizedFloralCard = memo<FloralCardProps>((
  props
) => {
  const {
    children,
    showWatercolorBackground = false,
    showCornerDecorations = true,
    ...restProps
  } = props;

  // Memoize the watercolor background to prevent re-renders
  const watercolorBackground = useMemo(() => {
    if (!showWatercolorBackground) return null;
    return <WatercolorBackground className="opacity-70" />;
  }, [showWatercolorBackground]);

  // Memoize corner decorations
  const cornerDecorations = useMemo(() => {
    if (!showCornerDecorations) return null;
    return (
      <>
        <FloralCornerDecoration position="top-left" size="sm" />
        <FloralCornerDecoration position="top-right" size="sm" />
        <FloralCornerDecoration position="bottom-left" size="sm" />
        <FloralCornerDecoration position="bottom-right" size="sm" />
      </>
    );
  }, [showCornerDecorations]);

  return (
    <FloralCard
      {...restProps}
      showWatercolorBackground={false}
      showCornerDecorations={false}
    >
      {watercolorBackground}
      {cornerDecorations}
      {children}
    </FloralCard>
  );
});

OptimizedFloralCard.displayName = 'OptimizedFloralCard';

// Lazy-loaded SVG component with intersection observer
interface LazySVGProps {
  src: string;
  alt?: string;
  className?: string;
  fallback?: React.ReactNode;
  onLoad?: () => void;
  onError?: (error: Error) => void;
}

export const LazySVG = memo<LazySVGProps>(({ 
  src, 
  alt, 
  className = '', 
  fallback,
  onLoad,
  onError 
}) => {
  const { targetRef, data, loading, error, hasIntersected } = useLazyAssetWithIntersection(
    src,
    { retryAttempts: 2, timeout: 3000 },
    { threshold: 0.1, rootMargin: '100px' }
  );

  // Notify parent components of load/error events
  React.useEffect(() => {
    if (data && onLoad) {
      onLoad();
    }
  }, [data, onLoad]);

  React.useEffect(() => {
    if (error && onError) {
      onError(error);
    }
  }, [error, onError]);

  if (!hasIntersected) {
    return (
      <div 
        ref={targetRef} 
        className={`${className} bg-gray-100 animate-pulse`}
        aria-label={alt || 'Loading image'}
      />
    );
  }

  if (loading) {
    return (
      <div 
        ref={targetRef}
        className={`${className} bg-gray-100 animate-pulse flex items-center justify-center`}
        aria-label="Loading..."
      >
        <div className="w-6 h-6 border-2 border-sage-300 border-t-sage-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div 
        ref={targetRef}
        className={`${className} bg-gray-50 flex items-center justify-center text-gray-400`}
        aria-label={`Failed to load: ${alt || 'image'}`}
      >
        {fallback || (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        )}
      </div>
    );
  }

  if (data) {
    return (
      <div 
        ref={targetRef}
        className={className}
        dangerouslySetInnerHTML={{ __html: data }}
        role={alt ? 'img' : 'presentation'}
        aria-label={alt}
      />
    );
  }

  return null;
});

LazySVG.displayName = 'LazySVG';

// Optimized header with lazy-loaded decorations
export const OptimizedFloralHeader = memo<FloralHeaderProps>(({ 
  bride, 
  groom, 
  date, 
  venue,
  showDivider = true,
  className = '',
  selectedLanguage = 'english'
}) => {
  // Memoize the header content to prevent re-renders when props haven't changed
  const headerContent = useMemo(() => (
    <div className={`text-center space-y-4 ${className}`}>
      <div className="space-y-2">
        {selectedLanguage === 'iban' ? (
          <>
            <p className="text-sage-600 text-sm sm:text-base font-medium tracking-wider uppercase text-center px-4">
              Enggau ati ti gaga, kami ngelaluka nuan
            </p>
            <p className="text-sage-600 text-sm sm:text-base font-medium tracking-wider uppercase text-center px-4">
              ngagai pengerami tikah
            </p>
          </>
        ) : (
          <>
            <p className="text-sage-600 text-sm sm:text-base font-medium tracking-wider uppercase text-center px-4">
              With hearts full of joy, you are invited
            </p>
            <p className="text-sage-600 text-sm sm:text-base font-medium tracking-wider uppercase text-center px-4">
              to the wedding of
            </p>
          </>
        )}
      </div>
      
      <div className="space-y-1">
        <h1 className="floral-title text-4xl md:text-5xl font-serif italic text-sage-800">
          {bride}
        </h1>
        <div className="text-3xl md:text-4xl font-serif italic text-sage-700">&</div>
        <h1 className="floral-title text-4xl md:text-5xl font-serif italic text-sage-800">
          {groom}
        </h1>
      </div>
      
      <div className="space-y-1 pt-4">
        <p className="text-lg font-serif text-sage-700">{date}</p>
        {venue && (
          <p className="text-md font-sans text-sage-600">{venue}</p>
        )}
      </div>
      
      {showDivider && <FloralDivider className="my-6" />}
    </div>
  ), [bride, groom, date, venue, showDivider, className, selectedLanguage]);

  return headerContent;
});

OptimizedFloralHeader.displayName = 'OptimizedFloralHeader';

// Performance-optimized form with memoized validation
interface OptimizedFormProps {
  children: React.ReactNode;
  onSubmit: (data: Record<string, any>) => void;
  validationSchema?: Record<string, (value: any) => string | null>;
  className?: string;
}

export const OptimizedForm = memo<OptimizedFormProps>(({ 
  children, 
  onSubmit, 
  validationSchema = {},
  className = '' 
}) => {
  const [formData, setFormData] = React.useState<Record<string, any>>({});
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  // Memoized validation function
  const validateField = useCallback((name: string, value: any) => {
    const validator = validationSchema[name];
    return validator ? validator(value) : null;
  }, [validationSchema]);

  // Memoized form data update
  const updateFormData = useCallback((name: string, value: any) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  }, [errors]);

  // Memoized validation for all fields
  const validateForm = useCallback(() => {
    const newErrors: Record<string, string> = {};
    let isValid = true;

    Object.keys(validationSchema).forEach(fieldName => {
      const error = validateField(fieldName, formData[fieldName]);
      if (error) {
        newErrors[fieldName] = error;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  }, [formData, validateField, validationSchema]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    
    if (validateForm()) {
      try {
        await onSubmit(formData);
      } catch (error) {
        // Handle error silently
      }
    }
    
    setIsSubmitting(false);
  }, [formData, onSubmit, validateForm, isSubmitting]);

  // Provide form context to children
  const formContext = useMemo(() => ({
    formData,
    errors,
    isSubmitting,
    updateFormData,
    validateField,
  }), [formData, errors, isSubmitting, updateFormData, validateField]);

  return (
    <form onSubmit={handleSubmit} className={className} noValidate>
      <FormContext.Provider value={formContext}>
        {children}
      </FormContext.Provider>
    </form>
  );
});

OptimizedForm.displayName = 'OptimizedForm';

// Form context for optimized form components
interface FormContextType {
  formData: Record<string, any>;
  errors: Record<string, string>;
  isSubmitting: boolean;
  updateFormData: (name: string, value: any) => void;
  validateField: (name: string, value: any) => string | null;
}

const FormContext = React.createContext<FormContextType | null>(null);

// Hook to use form context
export const useFormContext = () => {
  const context = React.useContext(FormContext);
  if (!context) {
    throw new Error('useFormContext must be used within OptimizedForm');
  }
  return context;
};

// Optimized input that connects to form context
interface OptimizedFormInputProps {
  name: string;
  label?: string;
  type?: string;
  placeholder?: string;
  required?: boolean;
  className?: string;
}

export const OptimizedFormInput = memo<OptimizedFormInputProps>(({ 
  name, 
  label, 
  type = 'text',
  placeholder,
  required = false,
  className = '' 
}) => {
  const { formData, errors, updateFormData, validateField } = useFormContext();

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    updateFormData(name, e.target.value);
  }, [name, updateFormData]);

  const handleBlur = useCallback(() => {
    const error = validateField(name, formData[name]);
    if (error) {
      // Update errors will be handled by the form validation
    }
  }, [name, formData, validateField]);

  return (
    <FloralInput
      label={label}
      type={type}
      value={formData[name] || ''}
      onChange={handleChange}
      onBlur={handleBlur}
      error={errors[name]}
      required={required}
      placeholder={placeholder}
      className={className}
    />
  );
});

OptimizedFormInput.displayName = 'OptimizedFormInput';

// Virtualized list component for large datasets
interface VirtualizedListProps<T> {
  items: T[];
  itemHeight: number;
  containerHeight: number;
  renderItem: (item: T, index: number) => React.ReactNode;
  className?: string;
}

export const VirtualizedList = memo(<T,>({
  items,
  itemHeight,
  containerHeight,
  renderItem,
  className = ''
}: VirtualizedListProps<T>) => {
  const [scrollTop, setScrollTop] = React.useState(0);
  
  const visibleStart = Math.floor(scrollTop / itemHeight);
  const visibleEnd = Math.min(
    visibleStart + Math.ceil(containerHeight / itemHeight) + 1,
    items.length
  );
  
  const visibleItems = useMemo(() => 
    items.slice(visibleStart, visibleEnd)
  , [items, visibleStart, visibleEnd]);
  
  const totalHeight = items.length * itemHeight;
  const offsetY = visibleStart * itemHeight;
  
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    setScrollTop(e.currentTarget.scrollTop);
  }, []);
  
  return (
    <div 
      className={`overflow-auto ${className}`}
      style={{ height: containerHeight }}
      onScroll={handleScroll}
    >
      <div style={{ height: totalHeight, position: 'relative' }}>
        <div style={{ transform: `translateY(${offsetY}px)` }}>
          {visibleItems.map((item, index) => (
            <div key={visibleStart + index} style={{ height: itemHeight }}>
              {renderItem(item, visibleStart + index)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}) as <T>(props: VirtualizedListProps<T>) => JSX.Element;

VirtualizedList.displayName = 'VirtualizedList';